import { FC, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AnalyticsGroupDto } from '@api/models/analytics/AnalyticsGroupDto';
import { AnalyticsMode } from '@api/models/analytics/AnalyticsMode';
import { AnalyticsResponseDto } from '@api/models/analytics/AnalyticsResponseDto';
import { AnalyticsFlag } from '@api/models/analytics/GroupingInfo';
import { ColumnMetaDto } from '@api/models/analytics/ColumnMetaDto';
import { ErrorAlert } from '../../common/ErrorAlert';
import { isNumericColumn } from '../analyticsColumns';
import { AnalyticsDomainConfig, GroupByOption } from '../analyticsDomainConfig';
import { AnalyticsGroupsChart, AnalyticsGroupsTable, AnalyticsRowsTable, AnalyticsView, ChartKind } from '.';
import { AnalyticsNotice } from './AnalyticsNotice';

export interface ExplorerBodyProps {
  config: AnalyticsDomainConfig;
  data: AnalyticsResponseDto | undefined;
  loading: boolean;
  hasError: boolean;
  view: AnalyticsView;
  chartKind: ChartKind;
  yKey: string;
  groupBy: string;
  chartGroupByOptions: GroupByOption[];
  noticeFlags: AnalyticsFlag[];
  noticeDismissed: boolean;
  hideNotice?: boolean;
  rowsPerPage: number;
  rowsPerPageDisabled?: boolean;
  visibleColumns: string[];
  onNoticeDismiss: () => void;
  onGroupByChange: (groupBy: string) => void;
  onYKeyChange: (yKey: string) => void;
  onChartKindChange: (kind: ChartKind) => void;
  onDrillDown?: (group: AnalyticsGroupDto) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}

const applyVisibleColumns = (metaColumns: ColumnMetaDto[], visible: string[]): ColumnMetaDto[] => {
  const byKey = new Map(metaColumns.map((c) => [c.key, c]));
  const ordered: ColumnMetaDto[] = [];
  for (const key of visible) {
    const col = byKey.get(key);
    if (col) ordered.push(col);
  }
  if (ordered.length > 0) return ordered;
  return metaColumns.filter((c) => c.defaultVisible);
};

export const ExplorerBody: FC<ExplorerBodyProps> = ({
  config,
  data,
  loading,
  hasError,
  view,
  chartKind,
  yKey,
  groupBy,
  chartGroupByOptions,
  noticeFlags,
  noticeDismissed,
  hideNotice,
  rowsPerPage,
  rowsPerPageDisabled,
  visibleColumns,
  onNoticeDismiss,
  onGroupByChange,
  onYKeyChange,
  onChartKindChange,
  onDrillDown,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t } = useTranslation();

  const isGroupsMode = data?.mode === AnalyticsMode.Groups;
  const groups = isGroupsMode ? (data.groups ?? []) : [];
  const metaColumns = data?.columns ?? [];
  const groupColumns = isGroupsMode ? applyVisibleColumns(metaColumns, visibleColumns) : [];
  // Chart axis options always come from the full metadata, not from the user's column-visibility
  // selection — hiding a metric column from the table should not remove it as a chart Y-axis option.
  const numericGroupColumns = isGroupsMode ? metaColumns.filter((c) => isNumericColumn(c.type)) : [];
  const validYKey = numericGroupColumns.some((c) => c.key === yKey) ? yKey : (numericGroupColumns[0]?.key ?? yKey);

  useEffect(() => {
    if (isGroupsMode && numericGroupColumns.length > 0 && validYKey !== yKey) {
      onYKeyChange(validYKey);
    }
  }, [isGroupsMode, numericGroupColumns.length, validYKey, yKey, onYKeyChange]);

  if (hasError) return <ErrorAlert />;
  if (!data) {
    return <AnalyticsGroupsTable groups={[]} columns={[]} groupBy="" loading={loading} empty />;
  }

  const NOTICE_FLAG_KEYS: Record<AnalyticsFlag, string> = {
    [AnalyticsFlag.WorkplaceOverlapping]: 'analytics.notice.workplace_inflates',
    [AnalyticsFlag.ActivityOverlapping]: 'analytics.notice.activity_inflates',
  };

  const noticeBlock = noticeFlags.length > 0 && !noticeDismissed && !hideNotice && (
    <AnalyticsNotice onDismiss={onNoticeDismiss}>
      <strong>{t('analytics.notice.title')}: </strong>
      {noticeFlags.map((flag, index) => (
        <Fragment key={flag}>
          {index > 0 && '\n\n'}
          {t(NOTICE_FLAG_KEYS[flag])}
        </Fragment>
      ))}
    </AnalyticsNotice>
  );

  if (data.mode === AnalyticsMode.Groups) {
    return (
      <>
        {noticeBlock}
        {view === 'chart' ? (
          <AnalyticsGroupsChart
            groups={groups}
            columns={metaColumns}
            yKey={validYKey}
            chartKind={chartKind}
            groupBy={groupBy}
            groupByOptions={chartGroupByOptions}
            onGroupByChange={onGroupByChange}
            onYKeyChange={onYKeyChange}
            onChartKindChange={onChartKindChange}
          />
        ) : (
          <AnalyticsGroupsTable
            groups={groups}
            columns={groupColumns}
            groupBy={groupBy}
            groupColumnLabelKey={config.groupByOptions.find((o) => o.value === groupBy)?.labelKey}
            onGroupClick={onDrillDown}
            loading={loading}
            empty={!loading && groups.length === 0}
          />
        )}
      </>
    );
  }

  const page = data.page;
  const rows = page?.content ?? [];
  const columns = applyVisibleColumns(metaColumns, visibleColumns);
  return (
    <>
      {noticeBlock}
      <AnalyticsRowsTable
        page={page}
        columns={columns}
        rowsPerPage={rowsPerPage}
        rowsPerPageDisabled={rowsPerPageDisabled}
        onPageChange={onPageChange}
        onSizeChange={onPageSizeChange}
        loading={loading}
        empty={!loading && rows.length === 0}
        getRowHref={config.getRowHref}
        getRowDrawerId={config.getRowDrawerId}
        DrawerBodyComponent={config.DrawerBodyComponent}
      />
    </>
  );
};
